<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { SupportMessageAuthorType } from '@/types/support'
import type { SupportTicket } from '@/types/support'
import SupportStatusBadge from './SupportStatusBadge.vue'

const props = defineProps<{
  ticket: SupportTicket
}>()

const { t } = useI18n()

const relativeTime = computed<string>(() => {
  const now = Date.now()
  const then = new Date(props.ticket.last_activity_at).getTime()
  const diff = Math.floor((now - then) / 1000)

  if (diff < 60) return t('support.relative_time.seconds_ago', { n: diff })
  if (diff < 3600) return t('support.relative_time.minutes_ago', { n: Math.floor(diff / 60) })
  if (diff < 86400) return t('support.relative_time.hours_ago', { n: Math.floor(diff / 3600) })
  if (diff < 86400 * 2) return t('support.relative_time.yesterday')

  return new Date(props.ticket.last_activity_at).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  })
})

const spokeMeta = computed<string>(() => {
  const count = props.ticket.message_count
  if (props.ticket.last_message_author_type === SupportMessageAuthorType.Organizer) {
    return t('support.list.spoke_you')
  }
  if (props.ticket.last_message_author_type === SupportMessageAuthorType.Staff) {
    return t('support.list.spoke_startline')
  }
  return t('support.list.spoke_awaiting')
})
</script>

<template>
  <RouterLink
    :to="`/organizer/support/${ticket.id}`"
    class="block rounded-xl bg-slate-800/50 border border-slate-700 px-5 py-4 hover:border-slate-600 transition-colors"
    :aria-label="ticket.subject"
  >
    <div class="flex items-start gap-3">
      <!-- Unread dot -->
      <div class="shrink-0 flex items-center justify-center w-4 pt-0.5">
        <span
          v-if="ticket.unread_by_organisation"
          class="w-2 h-2 rounded-full bg-emerald-400"
          :aria-label="t('support.list.unread')"
          role="img"
        />
      </div>

      <!-- Main content -->
      <div class="flex-1 min-w-0">
        <!-- Top row: category + status + time -->
        <div class="flex items-center gap-2 flex-wrap mb-1">
          <span class="text-xs text-slate-400 uppercase tracking-wide">
            {{ t(`support.category.${ticket.category}`) }}
          </span>
          <SupportStatusBadge :status="ticket.status" />
          <span class="ml-auto text-xs text-slate-500 shrink-0">{{ relativeTime }}</span>
        </div>

        <!-- Subject -->
        <p
          class="text-sm truncate"
          :class="ticket.unread_by_organisation
            ? 'text-white font-semibold'
            : 'text-slate-300 font-medium'"
        >
          {{ ticket.subject }}
        </p>

        <!-- Meta line -->
        <p class="text-xs text-slate-500 mt-0.5">
          {{ spokeMeta }} &middot; {{ t('support.list.message_count', ticket.message_count) }}
        </p>
      </div>
    </div>
  </RouterLink>
</template>
